/**
 * The same seam again, for the ear and the mouth.
 *
 * Voice is kept separate from the model on purpose. They are different
 * decisions with different costs: a family might want a good model answering and
 * the browser's free, private, offline voice doing the listening — or a small
 * local model and a better voice from elsewhere. Neither choice should force the
 * other, so there are two settings and two factories.
 *
 * The two interfaces hide a real difference in how services work. The browser
 * listens *live* and hands over words as they are spoken; a hosted service wants
 * a finished recording. Both are presented here as "start listening, stop
 * listening, here is what they said", because that is the only shape the panel
 * should have to care about.
 */
import type { Lang } from '../../domain/types';
import { audioContext } from './player';

export interface ListenHandlers {
  /** Words so far, while they are still talking. Only some services offer this. */
  onPartial?: (text: string) => void;
  /** What they said. Called once, at the end. */
  onFinal: (text: string) => void;
  /**
   * They are still making sound — for a service that cannot show words as they
   * come, this is how the silence watchdog knows not to cut them off.
   */
  onSound?: () => void;
  onError: (key: 'assistant.voice.denied' | 'assistant.voice.noMic' | 'assistant.voice.failed') => void;
}

export interface Listening {
  /** Finish and transcribe what was said. */
  stop(): void;
  /** Give up; nothing is transcribed. */
  cancel(): void;
}

export interface ListenOptions {
  /**
   * Keep listening through a pause instead of stopping at the end of the first
   * sentence.
   *
   * This is on for the assistant, and the reason is the people it is for:
   * somebody who is not sure how to phrase a question stops halfway through it,
   * thinks, and carries on. A recogniser that decides the sentence ended at the
   * first pause cuts them off mid-thought and submits half a question. So the
   * person decides when they have finished, by pressing the button again.
   */
  continuous?: boolean;
  /**
   * The longest one recording may run, for a service that records and then
   * transcribes. The default is a minute, which is right for a question and far
   * too short for somebody telling a whole recipe — see `features/recipes`.
   * Ignored by a service that listens live; that one ends when it is told to.
   */
  maxMs?: number;
}

export interface SpeechToText {
  readonly id: string;
  /** Whether this browser can do it at all. */
  available(): boolean;
  /** True while it is listening and showing words as they come. */
  readonly live: boolean;
  start(lang: Lang, handlers: ListenHandlers, opts?: ListenOptions): Promise<Listening>;
}

export interface TextToSpeech {
  readonly id: string;
  available(): boolean;
  speak(text: string, lang: Lang, voiceName?: string): Promise<void>;
  stop(): void;
  /** Voices to choose from, when the service has any. */
  voices(): Promise<Array<{ id: string; name: string }>>;
}

/** The BCP-47 tag a voice service expects. */
export function bcp47(lang: Lang): string {
  return lang === 'he' ? 'he-IL' : 'en-US';
}

/**
 * Record from the microphone until stopped, then hand over the blob.
 *
 * Shared by every service that transcribes a finished recording rather than
 * listening live. Deliberately plain: no chunked upload, no draft, no recovery —
 * this is one sentence asked of an assistant, not a grandmother's story, and the
 * app already has `features/stories/useRecorder.ts` for the thing that matters.
 */
export async function recordOnce(onStop: (audio: Blob) => void, onError: ListenHandlers['onError'], onSound?: () => void, maxMs: number = MAX_RECORDING_MS): Promise<Listening> {
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
    onError('assistant.voice.noMic');
    return { stop() {}, cancel() {} };
  }
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (e) {
    onError((e as Error)?.name === 'NotAllowedError' ? 'assistant.voice.denied' : 'assistant.voice.noMic');
    return { stop() {}, cancel() {} };
  }
  const chunks: Blob[] = [];
  const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((m) => MediaRecorder.isTypeSupported?.(m));
  // A long telling is recorded lighter — speech needs very little — so that five
  // minutes of it is a couple of megabytes to send rather than ten.
  const long = maxMs > MAX_RECORDING_MS;
  const rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), ...(long ? { audioBitsPerSecond: 48_000 } : {}) });
  let cancelled = false;
  const stopMeter = onSound ? watchLevel(stream, onSound) : () => {};
  // Nobody should be able to leave it recording by accident. What was said up to
  // the cap is still transcribed, never thrown away.
  const cap = setTimeout(() => {
    if (rec.state !== 'inactive') rec.stop();
  }, maxMs);
  rec.ondataavailable = (e) => {
    if (e.data.size) chunks.push(e.data);
  };
  rec.onstop = () => {
    clearTimeout(cap);
    stopMeter();
    for (const track of stream.getTracks()) track.stop();
    if (cancelled) return;
    onStop(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }));
  };
  rec.start();
  return {
    stop() {
      if (rec.state !== 'inactive') rec.stop();
    },
    cancel() {
      cancelled = true;
      if (rec.state !== 'inactive') rec.stop();
      else for (const track of stream.getTracks()) track.stop();
    },
  };
}

/** A question, not a speech. Something longer asks for longer (`ListenOptions.maxMs`). */
const MAX_RECORDING_MS = 60_000;

/** A filename whose extension matches what was really recorded — iPhones record mp4, not webm, and transcription services go by the name. */
export function recordingName(audio: Blob): string {
  const type = audio.type.toLowerCase();
  if (type.includes('mp4') || type.includes('m4a') || type.includes('aac')) return 'question.mp4';
  if (type.includes('ogg')) return 'question.ogg';
  if (type.includes('wav')) return 'question.wav';
  return 'question.webm';
}

/**
 * Report sound on the microphone, a few times a second, while there is any.
 *
 * The browser's own recogniser says when words arrive; a recording sent to a
 * server says nothing until it is over. Without this, the silence watchdog would
 * stop a recorded question four seconds after it started, however much of it was
 * left to say.
 *
 * If the level cannot be measured at all, it reports sound anyway, so the
 * recording ends when they press stop (or at the cap) rather than cutting them off.
 */
function watchLevel(stream: MediaStream, onSound: () => void): () => void {
  const ctx = audioContext();
  let source: MediaStreamAudioSourceNode | null = null;
  let analyser: AnalyserNode | null = null;
  if (ctx) {
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
    try {
      source = ctx.createMediaStreamSource(stream);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
    } catch {
      source = null;
      analyser = null;
    }
  }
  const samples = new Float32Array(1024);
  const timer = setInterval(() => {
    if (!analyser || !ctx || ctx.state !== 'running') {
      onSound();
      return;
    }
    analyser.getFloatTimeDomainData(samples);
    let sum = 0;
    for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
    // Speech sits well above 0.02 RMS; a quiet room sits well below it.
    if (Math.sqrt(sum / samples.length) > 0.02) onSound();
  }, 150);
  return () => {
    clearInterval(timer);
    try {
      source?.disconnect();
    } catch {
      /* already gone */
    }
  };
}
